import { CheckCircle, Copy, Edit, ExternalLink, Plus } from 'lucide-react'
import { FaFacebook } from 'react-icons/fa'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import {
  facebookAdsCampaignManagement,
  type FacebookAdsCampaignManagementResult,
} from 'sprites-types/workflow/tools/facebookAdsCampaignManagement'
import { ContextMenuWrapper, type ContextAction } from '../shared/ContextMenuWrapper'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'
import { SchemaValidationError } from '../shared/safeParseToolResult'

function FacebookAdsCampaignManagementDisplayComponent({
  nodeExecution,
}: WorkflowToolComponentProps) {
  const schema = getToolResultSchema(facebookAdsCampaignManagement, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const result = parseResult.data as FacebookAdsCampaignManagementResult
  const isCreate = 'created' in result && result.created
  const name = 'name' in result ? result.name : undefined
  const status = 'status' in result ? result.status : undefined
  const adAccountId = 'adAccountId' in result ? result.adAccountId : undefined

  // Extract metadata from inputs
  const inputs = nodeExecution.inputs.functionArgs as any
  const params = inputs?.action?.params || {}
  const objective = params.objective
  const dailyBudget = params.dailyBudget
  const lifetimeBudget = params.lifetimeBudget
  const specialAdCategories = params.specialAdCategories

  const formatBudget = (cents: number) => `$${(cents / 100).toFixed(2)}`

  // Strip 'act_' prefix if present for URL
  const accountIdForUrl = adAccountId?.replace(/^act_/, '')

  // Create filter_set parameter to search by campaign ID
  const filterSet = `SEARCH_BY_CAMPAIGN_GROUP_ID-STRING\x1EEQUAL\x1E"${result.id}"`

  const adsManagerUrl = accountIdForUrl
    ? `https://business.facebook.com/adsmanager/manage/campaigns?act=${accountIdForUrl}&selected_campaign_ids=${result.id}&filter_set=${encodeURIComponent(filterSet)}`
    : null

  const contextActions: ContextAction[] = [
    ...(adsManagerUrl
      ? [
          {
            id: 'open-ads-manager',
            label: 'Open in Ads Manager',
            icon: <ExternalLink className="h-4 w-4" />,
            onClick: () => window.open(adsManagerUrl, '_blank', 'noopener,noreferrer'),
          },
        ]
      : []),
    {
      id: 'copy-id',
      label: 'Copy Campaign ID',
      icon: <Copy className="h-4 w-4" />,
      onClick: () => navigator.clipboard.writeText(result.id),
    },
    ...(adsManagerUrl
      ? [
          {
            id: 'copy-url',
            label: 'Copy Ads Manager URL',
            icon: <Copy className="h-4 w-4" />,
            onClick: () => navigator.clipboard.writeText(adsManagerUrl),
          },
        ]
      : []),
  ]

  return (
    <ContextMenuWrapper actions={contextActions}>
      <div className="rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-xl backdrop-blur-[8px]">
        <div className="flex items-start gap-4 px-4 py-3">
          <div className="mt-1 flex-shrink-0">
            <div className="rounded-full bg-blue-100 p-2">
              {isCreate ? (
                <Plus className="h-5 w-5 text-blue-600" />
              ) : (
                <Edit className="h-5 w-5 text-blue-600" />
              )}
            </div>
          </div>
          <div className="min-w-0 flex-1">
            <div className="mb-2 flex items-center gap-2">
              <FaFacebook className="h-4 w-4 text-[#0064e0]" />
              <div className="text-sm font-semibold text-gray-900">
                {isCreate ? 'Campaign Created' : 'Campaign Updated'}
              </div>
            </div>

            <div className="rounded-2xl border border-gray-200 bg-gray-50 px-3 py-2">
              {name && <div className="mb-1 text-sm font-medium text-gray-900">{name}</div>}
              <div className="space-y-1 text-xs">
                <div className="text-gray-500">Campaign ID: {result.id}</div>
                {adAccountId && <div className="text-gray-500">Ad Account: {adAccountId}</div>}
                {objective && (
                  <div className="text-gray-700">
                    Objective:{' '}
                    <span className="font-medium">{objective.replace('OUTCOME_', '')}</span>
                  </div>
                )}
                {(dailyBudget || lifetimeBudget) && (
                  <div className="text-gray-700">
                    Budget:{' '}
                    {dailyBudget && (
                      <span className="font-medium">{formatBudget(dailyBudget)}/day</span>
                    )}
                    {dailyBudget && lifetimeBudget && <span> • </span>}
                    {lifetimeBudget && (
                      <span className="font-medium">{formatBudget(lifetimeBudget)} lifetime</span>
                    )}
                  </div>
                )}
                {specialAdCategories &&
                  specialAdCategories.length > 0 &&
                  specialAdCategories[0] !== 'NONE' && (
                    <div className="text-gray-700">
                      Special Categories:{' '}
                      <span className="font-medium">{specialAdCategories.join(', ')}</span>
                    </div>
                  )}
              </div>
              {status && (
                <div className="mt-2 flex items-center gap-2">
                  <span
                    className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                      status === 'ACTIVE'
                        ? 'bg-green-100 text-green-800'
                        : status === 'PAUSED'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {status}
                  </span>
                </div>
              )}
              {adsManagerUrl && (
                <div className="mt-2">
                  <a
                    href={adsManagerUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                  >
                    <ExternalLink className="h-3 w-3" />
                    <span>View in Ads Manager</span>
                  </a>
                </div>
              )}
            </div>

            {isCreate && (
              <div className="mt-2 flex items-center gap-1 text-xs text-gray-500">
                <CheckCircle className="h-3 w-3 text-green-600" />
                <span>Campaign is ready to use</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </ContextMenuWrapper>
  )
}

function downloadFacebookAdsCampaignManagement(_nodeExecution: NodeExecution): Promise<void> {
  return Promise.resolve()
}

function facebookAdsCampaignManagementToMarkdown(nodeExecution: NodeExecution): string {
  const schema = getToolResultSchema(facebookAdsCampaignManagement, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return '## Error: Schema validation failed\n\nUnable to export results due to data structure mismatch.'
  }

  const result = parseResult.data as FacebookAdsCampaignManagementResult
  const isCreate = 'created' in result && result.created

  const name = 'name' in result ? result.name : undefined
  const status = 'status' in result ? result.status : undefined
  const adAccountId = 'adAccountId' in result ? result.adAccountId : undefined

  // Extract metadata from inputs
  const inputs = nodeExecution.inputs as any
  const params = inputs?.action?.params || {}
  const objective = params.objective
  const dailyBudget = params.dailyBudget
  const lifetimeBudget = params.lifetimeBudget
  const specialAdCategories = params.specialAdCategories

  const formatBudget = (cents: number) => `$${(cents / 100).toFixed(2)}`

  let markdown = `## Facebook Campaign ${isCreate ? 'Created' : 'Updated'}\n\n`
  if (name) markdown += `**Name:** ${name}\n`
  markdown += `**Campaign ID:** ${result.id}\n`
  if (adAccountId) markdown += `**Ad Account:** ${adAccountId}\n`
  if (objective) markdown += `**Objective:** ${objective.replace('OUTCOME_', '')}\n`
  if (dailyBudget || lifetimeBudget) {
    markdown += '**Budget:** '
    if (dailyBudget) markdown += `${formatBudget(dailyBudget)}/day`
    if (dailyBudget && lifetimeBudget) markdown += ' • '
    if (lifetimeBudget) markdown += `${formatBudget(lifetimeBudget)} lifetime`
    markdown += '\n'
  }
  if (specialAdCategories && specialAdCategories.length > 0 && specialAdCategories[0] !== 'NONE') {
    markdown += `**Special Categories:** ${specialAdCategories.join(', ')}\n`
  }
  if (status) markdown += `**Status:** ${status}\n`
  markdown += '\n---\n'

  return markdown
}

export const FacebookAdsCampaignManagementDisplay = createWorkflowToolComponent(
  FacebookAdsCampaignManagementDisplayComponent,
  downloadFacebookAdsCampaignManagement,
  facebookAdsCampaignManagementToMarkdown
)
